import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus, Pencil, Trash2, Star, Loader2, Home, Eye, MapPinned } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { LocationPicker } from "@/components/LocationPicker";
import { StudentAddressPreview } from "@/components/StudentAddressPreview";

const EMPTY = { label: "", region: "", commune: "", address: "", address_reference: "", lat: null, lng: null };

function AddressDialog({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial?.id ? { ...EMPTY, ...initial } : EMPTY);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.label.trim() || !f.region || !f.commune || !f.address.trim()) { toast.error("Completa nombre, región, comuna y dirección"); return; }
    if (f.lat == null || f.lng == null) { toast.error("Confirma tu ubicación: usa “Ubicar en el mapa” y toca o arrastra el pin hasta tu taller"); return; }
    setBusy(true);
    try {
      const body = { label: f.label, region: f.region, commune: f.commune, address: f.address,
        address_reference: f.address_reference || null, lat: f.lat, lng: f.lng };
      const { data } = initial?.id ? await api.put(`/business/addresses/${initial.id}`, body) : await api.post("/business/addresses", body);
      toast.success(initial?.id ? "Dirección actualizada" : "Dirección agregada");
      onSaved(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="address-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? "Editar dirección" : "Nueva dirección"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nombre de la dirección <span className="text-destructive">*</span></Label>
            <Input value={f.label} maxLength={40} onChange={(e) => setF({ ...f, label: e.target.value.slice(0, 40) })} placeholder="ej. Mi casa, Mi Taller, Cowork" data-testid="address-label-input" />
            <p className="text-xs text-muted-foreground mt-1">Así la reconocerás al crear tus talleres.</p></div>
          <LocationPicker value={f} onChange={(v) => setF((s) => ({ ...s, ...v }))} />
          {f.lat != null && f.lng != null && <StudentAddressPreview a={f} testId="address-dialog-preview" />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={busy} data-testid="address-save-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar dirección"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddressesManager({ onChanged }) {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [hasPublished, setHasPublished] = useState(false);
  useEffect(() => {
    api.get("/business/addresses").then((r) => setList(r.data));
    api.get("/business/workshops").then((r) => setHasPublished(r.data.some((w) => w.active))).catch(() => {});
  }, []);
  const apply = (data) => { setList(data); setEditing(null); onChanged?.(); };
  const act = async (fn, msg) => {
    try { const { data } = await fn(); toast.success(msg); apply(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!list) return <div className="py-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  const anyUnmapped = list.some((a) => a.lat == null || a.lng == null);
  return (
    <div className="mt-6 space-y-5" data-testid="addresses-tab">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-xl font-extrabold break-words" data-testid="addresses-title">Mis direcciones</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl" data-testid="addresses-intro">Puedes registrar varias direcciones (tu casa, un taller particular o un cowork). Al momento de crear o publicar un taller, solo selecciona la ubicación que corresponda. Tu dirección habitual aparecerá seleccionada por defecto.</p>
        </div>
        <IconAction icon={Plus} label="Agregar dirección" onClick={() => setEditing({})} testId="address-add-btn"
          className="w-12 h-12 bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg" iconClass="w-6 h-6" />
      </div>
      {hasPublished && anyUnmapped && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="addresses-nomap-warning">
          <MapPinned className="w-4 h-4 shrink-0" />
          <span>Ubica la dirección en el mapa para que los tickets de tus alumnos muestren cómo llegar con Google Maps y Waze.</span>
        </div>
      )}
      <div className="flex flex-col gap-4 max-w-2xl">
        {list.map((a) => (
          <AddressCard key={a.id} a={a} canDelete={list.length > 1}
            onDefault={() => act(() => api.post(`/business/addresses/${a.id}/default`), "Dirección predeterminada actualizada")}
            onPreview={() => setPreview(a)} onEdit={() => setEditing(a)}
            onDelete={() => { if (window.confirm("¿Eliminar esta dirección?")) act(() => api.delete(`/business/addresses/${a.id}`), "Dirección eliminada"); }} />
        ))}
      </div>
      {editing && <AddressDialog initial={editing} onClose={() => setEditing(null)} onSaved={apply} />}
      {preview && (
        <Dialog open onOpenChange={(o) => !o && setPreview(null)}>
          <DialogContent className="w-[calc(100vw-2rem)] max-w-md" data-testid="address-preview-dialog">
            <DialogHeader><DialogTitle>Vista previa del alumno</DialogTitle>
              <DialogDescription>Así aparece “{preview.label || "Principal"}” en el ticket de tus alumnos.</DialogDescription></DialogHeader>
            <StudentAddressPreview a={preview} testId="address-list-preview" />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function IconAction({ icon: Icon, label, onClick, testId, className = "", iconClass = "w-4 h-4", disabled }) {
  const [tip, setTip] = useState(false);
  const t = useRef();
  const flash = () => { setTip(true); clearTimeout(t.current); t.current = setTimeout(() => setTip(false), 1500); };
  return (
    <span className="relative group shrink-0" onTouchStart={flash}>
      <button type="button" onClick={onClick} aria-label={label} disabled={disabled} data-testid={testId}
        className={`grid place-items-center w-9 h-9 rounded-full transition-colors disabled:opacity-40 ${className || "text-muted-foreground hover:text-primary hover:bg-primary/10"}`}>
        <Icon className={iconClass} />
      </button>
      <span role="tooltip" className={`pointer-events-none absolute right-0 top-full mt-1.5 z-20 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-xs font-medium text-background shadow-md transition-opacity ${tip ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>{label}</span>
    </span>
  );
}

function AddressCard({ a, canDelete, onDefault, onPreview, onEdit, onDelete }) {
  const mapped = a.lat != null && a.lng != null;
  return (
    <Card className={`p-4 flex flex-col gap-3 min-w-0 ${a.is_default ? "border-primary/40 bg-primary/5" : ""}`} data-testid={`address-item-${a.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h4 className="font-bold break-words">{a.label || "Principal"}</h4>
          {a.is_default && <p className="text-xs font-semibold text-primary" data-testid="address-default-badge">Predeterminada</p>}
        </div>
        <IconAction icon={Star} label={a.is_default ? "Dirección predeterminada" : "Marcar como predeterminada"}
          onClick={a.is_default ? undefined : onDefault} testId={`address-default-${a.id}`}
          className={a.is_default ? "text-sun cursor-default" : "text-muted-foreground hover:text-sun hover:bg-sun/10"}
          iconClass={`w-5 h-5 ${a.is_default ? "fill-sun" : ""}`} />
      </div>
      <p className="text-sm text-muted-foreground flex items-start gap-1.5"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
        <span className="break-words">{[a.address, a.commune, a.region].filter(Boolean).join(", ")}</span></p>
      {!mapped && (
        <p className="text-xs font-medium flex items-center gap-1.5 text-amber-700" data-testid={`address-nomap-${a.id}`}>
          <MapPinned className="w-3.5 h-3.5" />Sin ubicación en el mapa</p>
      )}
      <div className="flex items-center justify-end gap-1 border-t pt-2 mt-auto">
        <IconAction icon={MapPinned} label={mapped ? "Ajustar ubicación en el mapa" : "Asociar al mapa"} onClick={onEdit} testId={`address-map-${a.id}`}
          className={mapped ? "" : "text-amber-600 bg-amber-50 hover:bg-amber-100"} />
        <IconAction icon={Eye} label="Vista previa del alumno" onClick={onPreview} testId={`address-preview-${a.id}`} />
        <IconAction icon={Pencil} label="Editar dirección" onClick={onEdit} testId={`address-edit-${a.id}`} />
        {canDelete && <IconAction icon={Trash2} label="Eliminar dirección" onClick={onDelete} testId={`address-delete-${a.id}`}
          className="text-destructive hover:bg-destructive/10" />}
      </div>
    </Card>
  );
}

export function AddressSelect({ value, onChange, testId }) {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/business/addresses").then((r) => setList(r.data)).catch(() => {}); }, []);
  const def = list.find((a) => a.is_default) || list[0];
  useEffect(() => { if (!value && def && def.id !== "default") onChange(def.id); }, [def]); // eslint-disable-line
  const label = (
    <div className="flex items-center justify-between gap-2">
      <Label>Dirección donde harás el taller</Label>
      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs font-semibold" title="Direcciones registradas" data-testid={`${testId}-count`}>
        <Home className="w-3.5 h-3.5" />{list.length}
      </span>
    </div>
  );
  if (list.length <= 1) {
    return def ? (
      <div>
        {label}
        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1" data-testid={`${testId}-single`}>
          <MapPin className="w-3.5 h-3.5 shrink-0" />{[def.address, def.commune].filter(Boolean).join(", ")}
        </p>
      </div>
    ) : null;
  }
  return (
    <div>
      {label}
      <select value={value || def?.id || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
        {list.map((a) => <option key={a.id} value={a.id}>{a.label || "Principal"} — {[a.address, a.commune].filter(Boolean).join(", ")}{a.is_default ? " (predeterminada)" : ""}</option>)}
      </select>
    </div>
  );
}
